import { useEffect, useState } from 'react';
import { Modal, Form, Input, Checkbox, Button, Spin, Select } from 'antd-v5';
import { getCountriesList, getStatesList, getCitiesList } from '@/service';
import { loginUser } from '@/utils/cookie';
import styles from './index.module.scss';
import type { AddressFormData } from '../../hooks/types';
import { useLang } from '@/i18n';
import getLocal from '@/app/utils/getLocal';
import gtm from '@/utils/gtm';

const LOCALE_COUNTRY_MAP: Record<string, string> = {
    us: 'United States',
    ca: 'Canada',
};

interface AddressModalProps {
    open: boolean;
    locale?: string;
    onCancel?: () => void;
    /** 表单提交回调，透传完整地址表单数据 */
    onSuccess?: (data: AddressFormData) => void;
    loading?: boolean;
    error?: string | null;
}

interface SelectOption {
    key: number;
    label: string;
    value: string;
}

const AddressModal: React.FC<AddressModalProps> = ({ open, onCancel, onSuccess, loading, error }) => {
    const { t } = useLang('AddressModal');
    const { locale } = getLocal();
    const [form] = Form.useForm<AddressFormData>();

    const [countries, setCountries] = useState<SelectOption[]>([]);
    const [states, setStates] = useState<SelectOption[]>([]);
    const [cities, setCities] = useState<SelectOption[]>([]);

    const fetchCountries = async () => {
        try {
            const res: any = await getCountriesList({ page: 1, size: 300 }, locale);
            if (res?.code === '0') {
                setCountries(
                    res.data?.content?.map((item: any) => ({
                        key: item.id,
                        label: item.name,
                        value: item.name,
                    })) ?? [],
                );
            }
        } catch {
            /* ignore */
        }
    };

    const fetchStates = async (countryId: number) => {
        try {
            const res: any = await getStatesList({ countryId, page: 1, size: 300 }, locale);
            if (res?.code === '0') {
                setStates(
                    res.data?.content?.map((item: any) => ({
                        key: item.id,
                        label: item.name,
                        value: item.name,
                    })) ?? [],
                );
            }
        } catch {
            /* ignore */
        }
    };

    const fetchCities = async (stateId: number) => {
        try {
            const res: any = await getCitiesList({ stateId, page: 1, size: 3000 }, locale);
            if (res?.code === '0') {
                setCities(
                    res.data?.content?.map((item: any) => ({
                        key: item.id,
                        label: item.name,
                        value: item.name,
                    })) ?? [],
                );
            }
        } catch {
            /* ignore */
        }
    };

    const onCountryChange = (_val: string, option: any) => {
        form.setFieldsValue({ state: undefined, city: undefined });
        setStates([]);
        setCities([]);
        fetchStates(option.key);
    };

    const onStateChange = (_val: string, option: any) => {
        form.setFieldsValue({ city: undefined });
        setCities([]);
        fetchCities(option.key);
    };

    useEffect(() => {
        if (!open) return;

        const userInfo = loginUser();
        if (userInfo?.user?.email) {
            form.setFieldValue('email', userInfo.user.email);
        }

        const expectedCountry = LOCALE_COUNTRY_MAP[locale];
        if (expectedCountry) {
            fetchCountries().then(() => {
                setCountries((prev) => {
                    const matched = prev.find((c) => c.value === expectedCountry);
                    if (matched) {
                        form.setFieldValue('country', matched.value);
                        fetchStates(matched.key);
                    }
                    return prev;
                });
            });
        } else {
            fetchCountries();
        }
    }, [open]);

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            onSuccess?.(values);
            gtm.push({
                event: 'uaEvent',
                event_name: 'pre_order_submit',
                form_type: 'Service Renewal',
                form_info: 'Address',
                button_name: t('next_step'),
                status: 'success',
            });
        } catch {
            // validation failed, antd will show inline errors
        }
    };

    const handleCancel = () => {
        form.resetFields();
        setStates([]);
        setCities([]);
        onCancel?.();
    };

    return (
        <Modal
            className={styles.AddressModal}
            open={open}
            onCancel={handleCancel}
            footer={null}
            centered
            title="Device Return Address"
            maskClosable={false}
            afterClose={() => {
                form.resetFields();
                setStates([]);
                setCities([]);
            }}
        >
            <Spin spinning={false}>
                <Form form={form} layout="vertical" className="address-form">
                    <div className="form-row">
                        <Form.Item
                            label={t('first_name')}
                            name="firstName"
                            rules={[
                                { required: true, message: t('error_first_name') },
                                { max: 50, message: 'Max 50 characters' },
                            ]}
                        >
                            <Input placeholder="Value" />
                        </Form.Item>
                        <Form.Item
                            label={t('last_name')}
                            name="lastName"
                            rules={[
                                { required: true, message: t('error_last_name') },
                                { max: 50, message: 'Max 50 characters' },
                            ]}
                        >
                            <Input placeholder="Value" />
                        </Form.Item>
                    </div>

                    <Form.Item
                        label={t('phone')}
                        name="phone"
                        rules={[
                            { required: true, message: t('error_phone') },
                            { max: 50, message: 'Max 50 characters' },
                        ]}
                    >
                        <Input placeholder="Value" />
                    </Form.Item>

                    <Form.Item
                        label={t('email')}
                        name="email"
                        rules={[
                            { required: true, message: t('error_email') },
                            {
                                pattern: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,100}$/,
                                message: t('error_email_invalid'),
                            },
                        ]}
                    >
                        <Input placeholder="Value" type="email" />
                    </Form.Item>

                    <Form.Item label={t('zip_code')} name="zipCode" rules={[{ required: true, message: t('error_zip_code') }]}>
                        <Input placeholder="Value" />
                    </Form.Item>

                    <div className="form-row">
                        <Form.Item label={t('country')} name="country" rules={[{ required: true, message: t('error_country') }]}>
                            <Select
                                placeholder={t('country')}
                                options={countries}
                                onChange={onCountryChange}
                                showSearch
                                optionFilterProp="label"
                                disabled={!!LOCALE_COUNTRY_MAP[locale]}
                                getPopupContainer={(node) => node.parentNode as HTMLElement}
                            />
                        </Form.Item>
                        <Form.Item label={t('state')} name="state" rules={[{ required: true, message: t('error_state') }]}>
                            <Select
                                placeholder={t('state')}
                                options={states}
                                onChange={onStateChange}
                                showSearch
                                optionFilterProp="label"
                                getPopupContainer={(node) => node.parentNode as HTMLElement}
                            />
                        </Form.Item>
                    </div>
                    <Form.Item label={t('city')} name="city" rules={[{ required: true, message: t('error_city') }]}>
                        <Select
                            placeholder={t('city')}
                            options={cities}
                            showSearch
                            optionFilterProp="label"
                            getPopupContainer={(node) => node.parentNode as HTMLElement}
                        />
                    </Form.Item>
                    <Form.Item
                        label={t('street_address')}
                        name="address"
                        rules={[
                            { required: true, message: t('error_street_address') },
                            { max: 200, message: 'Max 200 characters' },
                        ]}
                    >
                        <Input placeholder="Value" />
                    </Form.Item>

                    <Form.Item
                        name="agreed"
                        valuePropName="checked"
                        rules={[
                            {
                                validator(_, value) {
                                    return value ? Promise.resolve() : Promise.reject('Please agree to the Privacy Policy and Terms of Use');
                                },
                            },
                        ]}
                    >
                        <Checkbox>
                            {t('agreed')}
                            <a href="https://account.ecoflow.com/agreement/en-us/PrivacyPolicy.html" target="_blank" rel="noreferrer">
                                {' '}
                                {t('privacy_policy')}{' '}
                            </a>
                            {t('and')}
                            <a href="https://us.ecoflow.com/pages/terms-of-use" target="_blank" rel="noreferrer">
                                {' '}
                                {t('terms_of_use')}
                            </a>
                        </Checkbox>
                    </Form.Item>

                    {error && <p className="form-error">{error}</p>}

                    <div className="form-footer">
                        <Button className="submit-btn" onClick={handleSubmit} block>
                            {t('next_step')}
                        </Button>
                    </div>
                </Form>
            </Spin>
        </Modal>
    );
};

export default AddressModal;
